import { Dialog } from '@angular/cdk/dialog';
import { KeyValuePipe } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  computed,
  inject,
  type OnInit,
  signal,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Organizations } from '@core/auth/organizations';
import { Routes } from '@core/routes/routes';
import { ButtonModule } from '@fino-ui/button';
import { FinoCommonModule } from '@fino-ui/common';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoIconModule } from '@fino-ui/icon';
import { FinoInputModule } from '@fino-ui/input';
import { ModalService } from '@fino-ui/modal';
import { NotificationService } from '@fino-ui/notification';
import { FinoSelectModule } from '@fino-ui/select';
import { FinoTranslationModule } from '@fino-ui/translation';
import Keycloak from 'keycloak-js';
import { merge } from 'lodash';
import { finalize, Subscription } from 'rxjs';
import { AppService } from '../../app.service';
import { DirectDebitsComponent } from '../../components/direct-debits/direct-debits.component';
import { IbanCheckComponent } from '../../components/iban-check/iban-check.component';
import { NavbarService } from '../../components/navbar/navbar.service';
import { PdfViewerComponent } from '../../components/pdf-viewer/pdf-viewer.component';
import { ReceivedPaymentsComponent } from '../../components/recieved-payments/recieved-payments.component';
import { StandingOrdersComponent } from '../../components/standing-orders/standing-orders.component';
import { TenantInfosComponent } from '../../components/tenant-infos/tenant-infos.component';
import { IdentifyDocumentService } from '../identify-document/identify-document.service';
import {
  DOCUMENT_STATUS,
  TRANSACTION_TYPES,
} from './capture-transaction.constants';
import {
  directDebitsForm,
  receivedPaymentsForm,
  standingOrdersForm,
} from './capture-transaction.form.constants';
import { CaptureTransactionService } from './capture-transaction.service';
import { ReportDocumentComponent } from './report-document/report-document.component';
import { SubmitModalComponent } from './submit-modal/submit-modal.component';
import { TransactionCounterComponent } from './transaction-counter/transaction-counter.component';
import { UnlockTransactionListComponent } from './unlock-transaction-list/unlock-transaction-list.component';

@Component({
  selector: 'app-capture-transaction',
  templateUrl: './capture-transaction.component.html',
  styleUrls: ['./capture-transaction.component.sass'],
  imports: [
    FinoCommonModule,
    UnlockTransactionListComponent,
    FinoTranslationModule,
    IbanCheckComponent,
    DirectDebitsComponent,
    ReceivedPaymentsComponent,
    StandingOrdersComponent,
    FormsModule,
    ReactiveFormsModule,
    FinoInputModule,
    FinoIconModule,
    ButtonModule,
    FinoSelectModule,
    TenantInfosComponent,
    KeyValuePipe,
    TransactionCounterComponent,
    ReportDocumentComponent,
    PdfViewerComponent,
  ],
})
export class CaptureTransactionComponent implements OnInit {
  private readonly captureTransactionService = inject(
    CaptureTransactionService,
  );
  private readonly identifyDocumentService = inject(IdentifyDocumentService);
  private readonly navbarService = inject(NavbarService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly appService = inject(AppService);
  private readonly modalService = inject(ModalService);
  private readonly dialog = inject(Dialog);
  private readonly notificationService = inject(NotificationService);

  public readonly pdfUrl = signal('');
  public readonly job = signal<any>(null);
  public readonly noJobLeft = signal(false);

  public modes = TRANSACTION_TYPES;

  public readonly selectedMode = signal<string>(
    TRANSACTION_TYPES.DIRECT_DEBITS,
  );
  public readonly sidebarInfos = signal<any>(null);

  public readonly isSubmitLoading = signal(false);

  public readonly showIbanCheck = signal(true);
  public readonly approvedWithCorrections = signal(false);
  public readonly extractionAccepted = signal(true);
  public readonly forceQA = signal(false);

  public readonly dontRenderNextJob = signal(false);
  public readonly useRotatedDocUrl = signal(false);
  public readonly userName = signal('');
  public readonly isSMMUser = signal(false);
  public unlockBalanceListSubscription: Subscription = Subscription.EMPTY;

  public readonly rotatedDocUrl = computed(() => this.job()?.rotatedDocUrl);

  public readonly statusIsClosed = computed(
    () => this.job()?.status === DOCUMENT_STATUS.CLOSED,
  );

  public readonly documentIsHidden = computed(() => this.job()?.hidden);

  public directDebitsForm: SubmittableForm = directDebitsForm;

  public receivedPaymentsForm: SubmittableForm = receivedPaymentsForm;

  public standingOrdersForm: SubmittableForm = standingOrdersForm;

  public loadNextDocumentForm: SubmittableForm = new SubmittableForm({
    loadNext: new FormControl(true),
  });

  public readonly transactions = signal<{
    directDebits: any[];
    receivedPayments: any[];
    standingOrders: any[];
  }>({ directDebits: [], receivedPayments: [], standingOrders: [] });

  private readonly authService = inject(Keycloak);

  ngOnInit() {
    this.route.params.subscribe((r) => {
      this.renderJob(r.id);
    });

    const userProfile = this.authService.tokenParsed;

    this.userName.set(userProfile?.email);
    this.isSMMUser.set(userProfile?.organization?.includes(Organizations.SMM));

    this.loadNextDocumentForm.valueChanges.subscribe((r) => {
      const { loadNext } = r;
      this.dontRenderNextJob.set(!loadNext);
    });
  }

  toggleRotatedDocUrl() {
    this.useRotatedDocUrl.set(!this.useRotatedDocUrl());
    this.pdfUrl.set(
      this.useRotatedDocUrl() ? this.rotatedDocUrl() : this.job().docUrl,
    );
  }

  get isQualityAssurance() {
    return this.route.snapshot.data.isQualityAssurance;
  }

  resetForms() {
    this.directDebitsForm.reset();
  }

  transactionChanged(_$event: any) {
    this.approvedWithCorrections.set(true);

    // trigger change detection for signal inputs
    this.transactions.update((transactions) => ({
      standingOrders: [...transactions.standingOrders],
      receivedPayments: [...transactions.receivedPayments],
      directDebits: [...transactions.directDebits],
    }));
  }

  ibanCheckSuccess($event: boolean) {
    this.showIbanCheck.set(!$event);
  }

  isValidDate(value: any): boolean {
    const date = new Date(value);
    return value && !Number.isNaN(date.getTime());
  }

  renderJob(id: string = '') {
    this.navbarService.loadCounts.next('balanceList');

    this.resetForms();
    this.captureTransactionService
      .fetchNextJob(id, this.isQualityAssurance)
      .subscribe({
        next: (result: any) => {
          if (!result) {
            this.noJobLeft.set(true);
            return;
          }
          this.job.set(result);
          this.appService.currentTicketId.set(result.id);
          this.showIbanCheck.set(true);
          this.forceQA.set(false);

          // used for qa
          this.extractionAccepted.set(true);
          this.approvedWithCorrections.set(false);

          const { capturedFinances, extractedFinances } = result;

          this.transactions.set({
            directDebits: [],
            receivedPayments: [],
            standingOrders: [],
          });

          if (capturedFinances) {
            const { directDebits, receivedPayments, standingOrders } =
              this.captureTransactionService.applyCapturedFinances(
                capturedFinances,
              );

            this.transactions.set({
              directDebits,
              receivedPayments,
              standingOrders,
            });
          } else if (extractedFinances) {
            const { directDebits, receivedPayments, standingOrders } =
              this.captureTransactionService.prepareFromFinctract(
                extractedFinances,
              );

            this.transactions.set({
              directDebits,
              receivedPayments,
              standingOrders,
            });
          }

          if (result.tenant) {
            this.identifyDocumentService
              .queryBLZ(result.tenant)
              .subscribe((res) => {
                this.sidebarInfos.set(merge(res, result));
              });
          }

          // when rotatedDocUrl is present, use it as default
          if (this.rotatedDocUrl()) {
            this.toggleRotatedDocUrl();
          } else {
            this.pdfUrl.set(result.docUrl);
          }
        },
        error: (error) => {
          if (error.status === 404) {
            this.noJobLeft.set(true);
            this.job.set(null);
          }
        },
      });
  }

  get hasTransactions() {
    return true;
  }

  openSubmitModal() {
    this.forceQA.set(false);
    this.modalService
      .addModal<boolean>(SubmitModalComponent)
      .subscribe((result) => {
        if (result) this.submit();
      });
  }

  openSubmitModalQA() {
    this.forceQA.set(true);
    this.modalService
      .addModal<boolean>(SubmitModalComponent)
      .subscribe((result) => {
        if (result) this.submit();
      });
  }

  unlockBalanceList() {
    this.unlockBalanceListSubscription = this.captureTransactionService
      .unlockBalanceList(this.job().id, this.isQualityAssurance)
      .subscribe((_res) => {
        this.router.navigate([Routes.Home]);
      });
  }

  renderNextJob() {
    this.route.params.subscribe((r) => {
      if (r.id) {
        this.router.navigate([
          this.isQualityAssurance
            ? Routes.CaptureTransactionQA
            : Routes.CaptureTransaction,
        ]);
      } else {
        this.renderJob();
      }
    });
  }

  submit() {
    const transactions = this.transactions();
    const data = this.captureTransactionService.prepareDataForSubmission(
      transactions.directDebits,
      transactions.receivedPayments,
      transactions.standingOrders,
      this.job(),
      this.isQualityAssurance,
      this.approvedWithCorrections(),
      this.extractionAccepted(),
      this.forceQA(),
    );

    this.isSubmitLoading.set(true);
    this.captureTransactionService
      .submitJob(this.job().id, data, this.isQualityAssurance)
      .pipe(finalize(() => this.isSubmitLoading.set(false)))
      .subscribe({
        next: (_res) => {
          this.dialog.closeAll();

          if (this.dontRenderNextJob()) {
            this.noJobLeft.set(true);
            return;
          }
          this.renderNextJob();
        },
        error: (resp: HttpErrorResponse) => {
          if (resp.status === 422 && resp.error.invalidCreditorIds != null) {
            return this.notificationService.error(
              `Found invalid creditor IDs: ${resp.error.invalidCreditorIds}`,
            );
          }
          this.notificationService.error(resp.message);
        },
      });
  }

  sendToAmetras() {
    this.extractionAccepted.set(false);
    this.submit();
  }

  selectMode(mode: string) {
    this.selectedMode.set(mode);
  }

  public readonly isPremiumBank = computed(() =>
    ['VB_DORTMUND', 'VR_FORCHHEIM'].includes(this.job()?.tenant),
  );

  public readonly premiumDocUrl = computed(
    () => `${window.location.href}${this.job()?.id}`,
  );
}
